/**
 * API Client pour les Environnements
 *
 * Endpoints:
 * - CRUD environnements
 * - Gestion des approbateurs
 * - Quotas
 */

import api from './index';

export interface Environment {
  id: string;
  name: string;
  displayName?: string;
  type: 'dev' | 'staging' | 'uat' | 'preprod' | 'prod' | 'dr' | 'custom';
  organization_id: string;
  description?: string;
  color?: string;
  icon?: string;
  sortOrder?: number;
  autoApprove?: boolean;
  requiresApproval?: boolean;
  approvalRequiredCount?: number;
  isProtected?: boolean;
  allowDestroy?: boolean;
  backendType?: string;
  backendConfig?: Record<string, any>;
  defaultVariables?: Record<string, any>;
  // Connexion cloud dédiée (null = utilise la connexion active par défaut)
  connection_service_id?: string | null;
  connection_service_name?: string | null;
  connection_service_provider?: string | null;
  created_at: string;
  updated_at?: string;
  approvers?: EnvironmentApprover[];
}

export interface EnvironmentApprover {
  userId: string;
  userName?: string;
  userEmail?: string;
  approvalOrder?: number;
  addedAt: string;
}

export interface EnvironmentQuota {
  plan: 'FREE' | 'STANDARD' | 'ENTERPRISE';
  used: number;
  limit: number | string;
  remaining: number | string;
  canCreate: boolean;
}

export interface CreateEnvironmentRequest {
  name: string;
  displayName?: string;
  type: 'dev' | 'staging' | 'uat' | 'preprod' | 'prod' | 'dr' | 'custom';
  description?: string;
  color?: string;
  icon?: string;
  sortOrder?: number;
  autoApprove?: boolean;
  requiresApproval?: boolean;
  approvalRequiredCount?: number;
  isProtected?: boolean;
  allowDestroy?: boolean;
  backendType?: string;
  backendConfig?: Record<string, any>;
  defaultVariables?: Record<string, any>;
  connectionServiceId?: string | null;
}

/**
 * Normalise un objet environnement : snake_case DB → camelCase frontend
 */
const normalizeEnvironment = (env: any): Environment => ({
  ...env,
  displayName: env.displayName ?? env.display_name,
  sortOrder: env.sortOrder ?? env.sort_order,
  requiresApproval: env.requiresApproval ?? env.requires_approval,
  autoApprove: env.autoApprove ?? env.auto_approve,
  approvalRequiredCount: env.approvalRequiredCount ?? env.approval_required_count,
  isProtected: env.isProtected ?? env.is_protected,
  allowDestroy: env.allowDestroy ?? env.allow_destroy,
  backendType: env.backendType ?? env.backend_type,
  backendConfig: env.backendConfig ?? env.backend_config,
  defaultVariables: env.defaultVariables ?? env.default_variables,
});

/**
 * Créer un environnement
 */
export const createEnvironment = async (data: CreateEnvironmentRequest): Promise<Environment> => {
  const response = await api.post('/api/environments', data);
  return normalizeEnvironment(response.data.environment);
};

/**
 * Lister tous les environnements
 */
export const listEnvironments = async (): Promise<Environment[]> => {
  const response = await api.get('/api/environments');
  return (response.data.environments || []).map(normalizeEnvironment);
};

/**
 * Obtenir un environnement
 */
export const getEnvironment = async (id: string): Promise<Environment> => {
  const response = await api.get(`/api/environments/${id}`);
  return normalizeEnvironment(response.data.environment);
};

/**
 * Mettre à jour un environnement
 */
export const updateEnvironment = async (
  id: string,
  data: Partial<CreateEnvironmentRequest>
): Promise<Environment> => {
  const response = await api.put(`/api/environments/${id}`, data);
  return normalizeEnvironment(response.data.environment);
};

/**
 * Supprimer un environnement
 */
export const deleteEnvironment = async (id: string): Promise<{ message: string }> => {
  const response = await api.delete(`/api/environments/${id}`);
  return response.data;
};

/**
 * Obtenir les approbateurs d'un environnement
 */
export const getApprovers = async (environmentId: string): Promise<EnvironmentApprover[]> => {
  const response = await api.get(`/api/environments/${environmentId}/approvers`);
  const rows = response.data.approvers || [];
  // Normalise snake_case DB → camelCase interface
  return rows.map((r: any) => ({
    userId: r.userId ?? r.user_id,
    userName: r.userName ?? r.full_name,
    userEmail: r.userEmail ?? r.email,
    approvalOrder: r.approvalOrder ?? r.approval_order,
    addedAt: r.addedAt ?? r.created_at,
  }));
};

/**
 * Ajouter un approbateur
 */
export const addApprover = async (
  environmentId: string,
  userId: string,
  approvalOrder?: number
): Promise<{ message: string }> => {
  const response = await api.post(`/api/environments/${environmentId}/approvers`, {
    userId,
    approvalOrder
  });
  return response.data;
};

/**
 * Retirer un approbateur
 */
export const removeApprover = async (
  environmentId: string,
  userId: string
): Promise<{ message: string }> => {
  const response = await api.delete(`/api/environments/${environmentId}/approvers/${userId}`);
  return response.data;
};

/**
 * Obtenir les quotas d'environnements
 */
export const getEnvironmentQuota = async (): Promise<EnvironmentQuota> => {
  const response = await api.get('/api/environments/quotas');
  return response.data.quotas;
};

export default {
  createEnvironment,
  listEnvironments,
  getEnvironment,
  updateEnvironment,
  deleteEnvironment,
  addApprover,
  removeApprover,
  getEnvironmentQuota
};
